import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { HardHat, Ladder, ShieldCheck, CheckCircle, PlayCircle, FileText } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  ladder: Ladder,
  ppe: HardHat,
};

export default function SafetyTraining() {
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchModules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/safety-modules`);
      setModules(data);
    } catch (error) {
      console.error('Error fetching safety modules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProgress = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/safety-modules/user/progress`, { withCredentials: true });
      setProgress(data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  }, []);

  useEffect(() => {
    fetchModules();
    if (user) {
      fetchProgress();
    }
  }, [user, fetchModules, fetchProgress]);

  const getModuleProgress = (moduleId) => progress.find((p) => p.module_id === moduleId);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={40} className="text-accent" weight="fill" />
            <h1 className="text-4xl sm:text-5xl font-bold text-primary" data-testid="safety-training-title">
              Safety Training Hub
            </h1>
          </div>
          <p className="text-base text-gray-600 max-w-3xl">
            OSHA-aligned safety training for pest control professionals. Complete each module,
            watch the training video, and pass the quiz to earn your completion record.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500" data-testid="safety-loading">Loading modules...</div>
        ) : modules.length === 0 ? (
          <div className="text-center py-20 text-gray-500" data-testid="safety-empty">No training modules available yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {modules.map((module) => {
              const Icon = MODULE_ICONS[module.module_type] || ShieldCheck;
              const moduleProgress = getModuleProgress(module.id);
              return (
                <Link
                  key={module.id}
                  to={`/safety-training/${module.id}`}
                  className="group bg-white border border-borders rounded-sm p-8 hover:border-accent hover:shadow-lg transition-all"
                  data-testid={`safety-module-card-${module.module_type}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-accent/10 p-4 rounded-sm">
                      <Icon size={36} className="text-accent" weight="duotone" />
                    </div>
                    {moduleProgress?.completed && (
                      <span className="flex items-center gap-1 text-sm text-green-700 bg-green-50 px-3 py-1 rounded-full" data-testid="module-completed-badge">
                        <CheckCircle size={16} weight="fill" /> Completed ({moduleProgress.quiz_score}%)
                      </span>
                    )}
                  </div>
                  <h2 className="text-lg font-semibold text-primary mb-2 group-hover:text-accent transition-colors">
                    {module.title}
                  </h2>
                  <p className="text-sm text-gray-600 mb-6">{module.description}</p>
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <FileText size={16} /> {module.content_sections.length} sections
                    </span>
                    {module.video_url && (
                      <span className="flex items-center gap-1">
                        <PlayCircle size={16} /> Training video
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <CheckCircle size={16} /> {module.quiz_questions.length}-question quiz
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!user && (
          <div className="mt-12 bg-accent/5 border border-accent/20 rounded-sm p-6 text-center">
            <p className="text-sm text-gray-700">
              <Link to="/login" className="text-accent font-semibold hover:underline" data-testid="safety-login-link">Log in</Link>
              {' '}to save your quiz scores and track training completion.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
